import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Mail, Clock, Linkedin, ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { COMPANY } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GENEVA_IMG =
  "https://images.unsplash.com/photo-1726334574781-19ab11a2ea78?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxnZW5ldmElMjBhcmNoaXRlY3R1cmUlMjBsdXh1cnl8ZW58MHx8fHwxNzkyMjc1NjI5fDA&ixlib=rb-4.1.0&q=85";

const initialForm = { name: "", email: "", phone: "", message: "" };

export default function Contact() {
  const { t } = useTranslation("contact");
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 10) {
      toast.error(t("form.toastShort"));
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        company: "",
        topic: "Premier rendez-vous",
        nda: false,
      });
      toast.success(t("form.toastSuccess"));
      setForm(initialForm);
    } catch (err) {
      toast.error(t("form.toastError"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32 lg:pt-40">
      <header className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
        <p className="overline-label mb-8 flex items-center gap-3">
          <span className="inline-block h-px w-10 bg-brass/60" />
          {t("hero.overline")}
        </p>
        <h1 className="max-w-3xl font-display text-4xl font-light leading-[1.08] tracking-tight text-chalk sm:text-5xl lg:text-6xl">
          <MaskedLines
            lines={[
              t("hero.titleA"),
              <span key="b" className="font-serif italic text-brass">{t("hero.titleB")}</span>,
            ]}
          />
        </h1>
        <p className="mt-8 max-w-2xl text-base leading-relaxed text-mist/85">{t("hero.lead")}</p>
      </header>

      <section className="mx-auto mt-16 max-w-7xl px-6 pb-24 sm:px-8 lg:px-12 lg:mt-20 lg:pb-32">
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="space-y-8">
              <ul className="space-y-0 border-t border-white/[0.08]">
                <li className="flex gap-5 border-b border-white/[0.08] py-6">
                  <MapPin size={18} className="mt-1 shrink-0 text-brass" />
                  <div>
                    <p className="overline-label mb-2">{t("info.addressLabel")}</p>
                    <address className="text-sm not-italic leading-relaxed text-mist/85">
                      {t("info.addressLine1")}<br />{t("info.addressLine2")}
                    </address>
                  </div>
                </li>
                <li className="flex gap-5 border-b border-white/[0.08] py-6">
                  <Mail size={18} className="mt-1 shrink-0 text-brass" />
                  <div>
                    <p className="overline-label mb-2">{t("info.emailLabel")}</p>
                    <a
                      href={`mailto:${COMPANY.email}`}
                      data-testid="contact-email-link"
                      className="text-sm text-mist/85 transition-colors duration-200 hover:text-chalk"
                    >
                      {COMPANY.email}
                    </a>
                  </div>
                </li>
                <li className="flex gap-5 border-b border-white/[0.08] py-6">
                  <Linkedin size={18} className="mt-1 shrink-0 text-brass" />
                  <div>
                    <p className="overline-label mb-2">LinkedIn</p>
                    <a
                      href={COMPANY.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-linkedin-link"
                      className="text-sm text-mist/85 transition-colors duration-200 hover:text-chalk"
                    >
                      linkedin.com/company/gabana-management-sa
                    </a>
                  </div>
                </li>
                <li className="flex gap-5 border-b border-white/[0.08] py-6">
                  <ExternalLink size={18} className="mt-1 shrink-0 text-brass" />
                  <div>
                    <p className="overline-label mb-2">Crunchbase</p>
                    <a
                      href={COMPANY.crunchbase}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-crunchbase-link"
                      className="text-sm text-mist/85 transition-colors duration-200 hover:text-chalk"
                    >
                      crunchbase.com/organization/gabana-management-sa
                    </a>
                  </div>
                </li>
                <li className="flex gap-5 border-b border-white/[0.08] py-6">
                  <Clock size={18} className="mt-1 shrink-0 text-brass" />
                  <div>
                    <p className="overline-label mb-2">{t("info.hoursLabel")}</p>
                    <p className="text-sm text-mist/85">{t("info.hours")}</p>
                  </div>
                </li>
              </ul>

              <div className="overflow-hidden border border-white/10">
                <img
                  src={GENEVA_IMG}
                  alt={t("info.imgAlt")}
                  className="h-56 w-full object-cover transition-transform duration-700 hover:scale-105"
                />
              </div>

              <div className="border border-white/[0.08] bg-surface/40 p-6 text-xs leading-relaxed text-slate2">
                <p>{t("info.rcLabel")} : <span className="font-mono text-chalk">{COMPANY.regNumber}</span></p>
                <p className="mt-1">{t("info.tvaLabel")} : <span className="font-mono text-chalk">{COMPANY.tva}</span></p>
                <p className="mt-1">{t("info.regLabel")} : {COMPANY.registered}</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={handleSubmit}
              className="border border-white/[0.08] bg-surface/40 p-8 lg:p-10"
            >
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="overline-label mb-3 block">{t("form.name")}</label>
                  <input
                    id="contact-name"
                    data-testid="contact-form-name-input"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder={t("form.namePlaceholder")}
                    className="field-input"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="overline-label mb-3 block">{t("form.email")}</label>
                  <input
                    id="contact-email"
                    data-testid="contact-form-email-input"
                    required
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder={t("form.emailPlaceholder")}
                    className="field-input"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="contact-phone" className="overline-label mb-3 block">{t("form.phone")}</label>
                  <input
                    id="contact-phone"
                    data-testid="contact-form-phone-input"
                    type="tel"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder={t("form.phonePlaceholder")}
                    className="field-input"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="contact-message" className="overline-label mb-3 block">{t("form.message")}</label>
                  <textarea
                    id="contact-message"
                    data-testid="contact-form-message-input"
                    required
                    rows={6}
                    value={form.message}
                    onChange={set("message")}
                    placeholder={t("form.messagePlaceholder")}
                    className="field-input resize-none"
                  />
                </div>
              </div>
              <button
                type="submit"
                data-testid="contact-form-submit-button"
                disabled={sending}
                className="btn-brass mt-8 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {sending ? t("form.sending") : t("form.submit")}
              </button>
              <p className="mt-5 text-xs leading-relaxed text-slate2">{t("form.privacy")}</p>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
